import React, { Fragment, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useGetServicesQuery } from "services/services";
import {
  useCreateRoleMutation,
  useEditRoleMutation,
  useGetSpecificRoleQuery,
} from "services/roles";
import { useSnackbar } from "notistack";
import TextField from "@mui/material/TextField";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Button from "@mui/material/Button";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useHistory } from "react-router-dom";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useDispatch } from "react-redux";
import { setRole } from "rtkSlices/metaDataSlice";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import GroupModal from "./groupModal";
import {
  computeDependencyClosure,
  findUngrantableAutoAssign,
  requiredEnforcedDepLevel,
  isEnforcedDepAsset,
  cellDepLock,
  moduleTabLock,
  moduleTabGateRank,
  moduleTabGateInfo,
  moduleTabSurfaceGateRank,
  moduleTabSurfaceGateInfo,
  gateRankFor,
  gateInfoFor,
  cascadeGates,
  diffPerms,
  permLabel,
  isScopingAsset,
  rank as permRank,
} from "utils/permissionDependencies";
import {
  clampUtilsToCaller,
  clampServicesToCaller,
  clampPermsToCallerHoldings,
  moduleFullBlocked,
  bulkFullBlocked,
} from "utils/callerClamp";
// Shared catalogue (extracted so roleEditorSerde + its round-trip tests consume the
// SAME data the matrix renders). `allUtils`/`assets` keep their local names.
import { ALL_UTILS as allUtils, ROLE_ASSETS as assets } from "utils/roleCatalogue";
import {
  loadModulesIntoUtils,
  loadServicesIntoPermissions,
  serializeEditorState,
  deriveAnchorGrant,
  editorChangeKey,
  removeExtraSpaces,
} from "utils/roleEditorSerde";
import {
  SHARE,
  RECEIVE,
  SWITCHES,
  SWITCH_MODULE,
  loadSwitches,
  switchOptions,
  cloneSeed,
  meetsShareCreation,
  shareSwitchesOff,
  SHARE_NEEDS_CREATION_HINT,
  SHARE_SWITCHED_OFF_NOTE,
  SHARE_VIEW_ONLY_NOTE,
} from "utils/sharingSwitches";


function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3, overflow: "hidden" }}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}
// The caller-holdings clamps (clampUtilsToCaller / clampServicesToCaller /
// clampPermsToCallerHoldings) live in utils/callerClamp.js since 2026-09-08 - wildcard-aware
// and unit-tested there.
// D26 (a) (2026-09-25): a level the editor cannot grant because of their OWN limits is hidden,
// not greyed. This invisible stand-in takes the hidden icon's place so the remaining level
// icons stay in their columns. Greys about the edited role itself ("Requires X", "Required
// by X", scoping columns, grant-all floors) stay greyed with their tooltips.
const HIDDEN_LEVEL_SLOT = (
  <CheckCircleOutlinedIcon sx={{ visibility: "hidden" }} aria-hidden="true" />
);
// Clone naming: "<base> (Clone)", then "<base> (Clone 2)", "(Clone 3)", … skipping any name
// already taken (case-insensitive). Strips a trailing "(Clone[ N])" off the source first so
// cloning a clone stays tidy: "X (Clone)" -> "X (Clone 2)", never "X (Clone) (Clone)".
function buildCloneName(sourceName, existingNames) {
  const base = String(sourceName || "")
    .replace(/\s*\(Clone(?:\s+\d+)?\)\s*$/i, "")
    .trim();
  const taken = new Set((existingNames || []).map((n) => String(n || "").toLowerCase()));
  const first = `${base} (Clone)`;
  if (!taken.has(first.toLowerCase())) return first;
  let n = 2;
  while (taken.has(`${base} (Clone ${n})`.toLowerCase())) n++;
  return `${base} (Clone ${n})`;
}

export default function EditRole(props) {
  let modules = [];
  const allServices = useSelector((state) => state.metaData?.services);
  const isUnscoped = useSelector((state) => state.metaData?.unscopedAdmin);
  const callerApps = useSelector((state) => state.metaData?.apps); // FE-4: caller's held modules
  // A user may VIEW but NEVER modify the role they themselves wear — for EVERYONE, including
  // full admins / ⊤ (the BE enforces the same: checkRoleUpdate/ServiceEdit/Delete deny a
  // self-edit; annotateEditable marks the own role editable:false). Changing a role must come
  // from a DIFFERENT user who holds it, or the master roles API (break-glass).
  const isOwnRole =
    !!props.selectedRole &&
    String(props.selectedRole._id) ===
      String(window.localStorage.getItem("role"));
  // Read-only when: editing your OWN role (anyone, incl. ⊤), OR a role at your SAME privilege
  // level (samePrivilege — a peer; the BE refuses peer edits), OR a non-holdable existing role
  // (editable:false) for a scoped admin (the BE would 403 on save anyway — disable Save to
  // avoid a dead-end). New/clone roles are never read-only.
  const readOnly =
    !props.isCloneRole &&
    !!props.selectedRole &&
    (isOwnRole ||
      props.selectedRole.samePrivilege === true ||
      (!isUnscoped && props.selectedRole.editable === false));
  const [isLoading, setIsLoading] = useState(false);
  const [groupModal, setGroupModal] = useState(false);
  const [serviceId, setServiceId] = useState("");
  // Item 5: cells auto-assigned by the dependency closure (amber highlight) + the caller's
  // own per-solution held ranks (to block ungrantable auto-assigns for a delegated admin).
  const [autoAssignedCells, setAutoAssignedCells] = useState(new Set());
  // Top-down model: cells auto deselected or lowered by the master-layer cascade (red tint).
  const [removedCells, setRemovedCells] = useState(new Set());
  // Impact preview: a gesture whose dependency effects reach beyond the clicked cell is
  // staged here and applied only on Proceed. { final, added, removed }
  const [pendingChange, setPendingChange] = useState(null);
  // Item: filter the solutions matrix rows by name.
  const [solutionSearch, setSolutionSearch] = useState("");
  // Item 5: true once the user actually touches a permission cell — so a name/description-only
  // edit of an (as-yet-unbackfilled) legacy role doesn't trigger the dependency closure/re-save.
  const [permsTouched, setPermsTouched] = useState(false);
  // Item 5b: the caller's OWN per-solution held ranks. Prefer the caller ROLE DOC
  // (props.callerRole, passed from roleManagement) — metaData.services strips the
  // Group Management tab, which would wrongly blank/cap it. Fall back to metaData.services
  // (degrade-open) when the role doc isn't available.
  const heldServiceTabs = React.useMemo(() => {
    const m = {};
    const src =
      (props.callerRole && props.callerRole.services) || allServices || [];
    src.forEach((s) => {
      const sid = s && (s.serviceId != null ? s.serviceId : s.id);
      if (sid == null) return;
      m[sid] = m[sid] || {};
      (s.tabs || []).forEach((t) => {
        if (t && t.name) m[sid][t.name] = permRank(t.permission);
      });
    });
    return m;
  }, [props.callerRole, allServices]);
  // B4: the caller's grant-all ('*') ranks per axis. '*' is now an ORDINARY value, so a
  // caller may hold it at READ or on one axis only — callerRankFor/callerModuleRankFor must
  // read its RANK (not treat any '*' as Infinity), or a READ-'*' caller would be offered ALL
  // everywhere (a privilege escalation) and a single-axis '*' caller would dead-end.
  const callerWildSolutionRank = React.useMemo(() => {
    const src = (props.callerRole && props.callerRole.services) || allServices || [];
    const w = src.find((s) => s && String(s.serviceId != null ? s.serviceId : s.id) === "*");
    return w ? Math.max(0, ...(w.tabs || []).map((t) => (t && t.name === "*" ? permRank(t.permission) : 0))) : 0;
  }, [props.callerRole, allServices]);
  const callerRankFor = (sid, asset) =>
    isUnscoped
      ? Infinity
      : Math.max(callerWildSolutionRank, (heldServiceTabs[sid] && heldServiceTabs[sid][asset]) || 0);
  // Item 5b/7: the caller's held MODULE tabs (utilities matrix), from the caller's own role
  // doc (complete, includes Solution Management field parts) with metaData.apps fallback.
  const heldModuleTabs = React.useMemo(() => {
    const m = {};
    const src = (props.callerRole && props.callerRole.modules) || callerApps || [];
    src.forEach((mod) => {
      if (!mod || !mod.name) return;
      m[mod.name] = m[mod.name] || {};
      (mod.tabs || []).forEach((t) => {
        if (t && t.name) m[mod.name][t.name] = permRank(t.permission);
      });
    });
    return m;
  }, [props.callerRole, callerApps]);
  const callerWildModuleRank = React.useMemo(() => {
    const src = (props.callerRole && props.callerRole.modules) || callerApps || [];
    const w = src.find((m) => m && m.name === "*");
    return w ? Math.max(0, ...(w.tabs || []).map((t) => (t && t.name === "*" ? permRank(t.permission) : 0))) : 0;
  }, [props.callerRole, callerApps]);
  const callerModuleRankFor = (moduleName, tabName) =>
    isUnscoped
      ? Infinity
      : Math.max(callerWildModuleRank, (heldModuleTabs[moduleName] && heldModuleTabs[moduleName][tabName]) || 0);
  // Item 5b/7 (visual hide): a non superadmin sees ONLY the utilities, tabs, solutions and
  // asset columns they themselves hold. Degrade OPEN: without a resolved caller role doc we
  // never blank the editor (the create-seed clamp + BE canGrant still prevent over-grants).
  const scopedEditor = !isUnscoped && !!props.callerRole;
  // Migration-window guard: a scoped caller whose own (normalized) role doc has
  // no native "Solution Features" row predates the taxonomy structure migration
  // — their feature holdings are not expressed yet, so scoped editing would
  // clamp everything feature-shaped to nothing and mint broken roles. Refuse to
  // open until the migration lands (superadmin editors are unaffected).
  const callerPredatesTaxonomy =
    scopedEditor &&
    // B4: a '*'-module caller implicitly holds every feature — never treat it as pre-taxonomy
    // (else a scoped '*' caller, e.g. READ-'*', would be refused the editor entirely).
    !((props.callerRole && props.callerRole.modules) || []).some((m) => m && m.name === "*") &&
    !((props.callerRole && props.callerRole.modules) || []).some(
      (m) => m && m.name === "Solution Features"
    );
  // A SUPERADMIN target role holds everything implicitly — its explicit tabs are legacy
  // artifacts, every backfill skipped it, and the BE gate exempts it. So the editor must
  // exempt it too: no gates, no dependency locks, no closures, no cascades. Without this,
  // touching any cell on a ⊤ role proposed cascading away everything its (absent) gate
  // tabs appear not to cover.
  // Super-admin removal: the anchor/root target role — whose "*" tabs are inert and
  // must skip the cascade/gate engine — is identified by protectedAnchor, the legacy
  // flag (transition), OR its wildcard grants ("*" module + "*" service).
  const roleIsAnchor = (r) =>
    !!(
      r &&
      (r.protectedAnchor ||
        r.unscopedAdmin ||
        ((r.modules || []).some((m) => m && m.name === "*") &&
          (r.services || []).some((s) => s && String(s.serviceId) === "*")))
    );
  const targetIsUnscoped = !!(
    roleIsAnchor(props.selectedRole) || roleIsAnchor(props.cloneRole)
  );
  // Column-header masters now reveal on CSS :hover (see masterHeaderSx) instead
  // of a React hover-state reducer — the old dispatchHoverState re-rendered the
  // whole per-solution matrix on every mouse enter/leave, which caused the lag
  // when going over the columns.

  if (props.selectedRole) {
    // Base-Permissions matrix seed — the pure, round-trip-tested transform in
    // roleEditorSerde. Drops off-catalogue modules/tabs (the intended deprecation
    // path), fills missing catalogue tabs as "disable", computes each row Overall.
    // The '*' module is NOT rendered as a matrix row — it is carried in anchorGrant.
    modules = loadModulesIntoUtils(props.selectedRole, allUtils);
  }
  const history = useHistory();
  let token = window.localStorage.getItem("token");
  const getServices = useGetServicesQuery({
    token,
    param: "?tenant=true&user=true",
  });
  const dispatch = useDispatch();
  const [createRole, createResult] = useCreateRoleMutation();
  const [updateRole, updateResult] = useEditRoleMutation();
  const [roleChanged, setRoleChanged] = React.useState(false);
  const [groupIds, setGroupIds] = useState(props.selectedRole ? patchGroupIds() : [])
  const roleRes = useGetSpecificRoleQuery(window.localStorage.getItem("role"), {
    skip: !roleChanged,
  });
  const [value, setValue] = React.useState(0);
  const [name, setName] = React.useState(
    props.isCloneRole
      ? buildCloneName(props.selectedRole.name, props.existingRoleNames)
      : props.selectedRole.name || ""
  );
  const [description, setDescription] = React.useState(
    props.selectedRole.description || ""
  );
  const { enqueueSnackbar } = useSnackbar();
  const [utils, setUtils] = React.useState(
    !props.selectedRole
      ? isUnscoped
        ? allUtils
        : // FE-4: create defaults to grantable modules. Prefer the caller's ROLE DOC —
          // metaData.apps lacks the Solution Management field-part tabs (those live in
          // partPermissions), which would wrongly seed held parts as disable.
          clampUtilsToCaller(
            allUtils,
            (props.callerRole && props.callerRole.modules) || callerApps
          )
      : modules
  );
  const [servicePermissions, setServicePermissions] = React.useState([]);
  // Anchor round-trip (Step 2): the '*' grant-all rows are carried as ordinary editor
  // state ({ base, solution } = the rank of the '*' tab on the '*' module / '*' service
  // row). Seeded from the BE-authoritative wildcardGrant (rows fallback). loadedAnchor
  // is the ORIGINAL value — lowering below it is a deliberate removal that needs the
  // confirmation dialog (allowDropWildcard) before serialize will drop the '*' rows.
  // State (not memo) so a successful in-session Update — which leaves the editor OPEN —
  // can re-baseline it to what is now persisted. Otherwise a second reduction in the same
  // session is measured against a stale "was" and either skips confirmation or hits a
  // spurious BE 409.
  const [loadedAnchorGrant, setLoadedAnchorGrant] = React.useState(() =>
    deriveAnchorGrant(props.selectedRole)
  );
  const [anchorGrant, setAnchorGrant] = React.useState(loadedAnchorGrant);
  // Per-axis confirmation that the user intends to REDUCE grant-all on that axis (the BE
  // LD-3 guard rejects any '*' rank reduction without body.dropWildcard). Per-axis (not a
  // single shared boolean) so confirming a base reduction never silently lets the solution
  // axis reduce without its own dialog.
  const [wildReduceOK, setWildReduceOK] = React.useState({ base: false, solution: false });
  // Pending grant-all reduction staged by the confirmation dialog: { axis, next } or null.
  const [pendingWildDrop, setPendingWildDrop] = React.useState(null);
  // Unsaved-changes tracking. The baseline is the role as loaded (create: the defaults;
  // clone: the copied role), and after a successful Update the role as saved. "Changed"
  // means the role that WOULD be saved differs from the baseline, so changing a cell and
  // changing it back is not a change. Reset restores the baseline; Update and Reset stay
  // disabled until there is a change; leaving with changes asks first.
  // Solution sharing switches (Share / Receive Shared Solutions): shown as two cells in the Solution
  // Management row, right after its own tabs (owner decision S6-1 B), but kept apart from the Base Permissions
  // matrix state and merged into that row on save (utils/sharingSwitches.js).
  // Edit: the role's named levels. Clone (a create): at most what this editor may switch on. Create: Off.
  const sharingOn = props.solutionSharing === true;
  const editorModules = (props.callerRole && props.callerRole.modules) || callerApps || [];
  const switchCtx = { editorModules, editorGrantAll: !!isUnscoped, sharingOn };
  const sourceSwitches = props.selectedRole ? loadSwitches(props.selectedRole) : { [SHARE]: 0, [RECEIVE]: 0 };
  const [switchSeed] = React.useState(() =>
    props.isCloneRole ? cloneSeed(sourceSwitches, switchCtx) : sourceSwitches
  );
  const [switches, setSwitches] = React.useState(switchSeed);
  // The clone note compares the copied role with the seed, never with later clicks.
  const cloneLoweredSwitches =
    !!props.isCloneRole && SWITCHES.some((tab) => sourceSwitches[tab] > switchSeed[tab]);
  // O23 (owner 2026-10-21): Share Solutions needs Solution Creation at Full on this role. ONE rule over
  // the committed state, never hooked per gesture: whenever the role stops meeting it (its tab, a row's
  // Overall, a Solution Edit part or page through R2, the Administration master through R1, a grant-all
  // reduction, Reset, Reload, or a role loaded that way), Share goes Off and the note shows. It is never
  // folded into the starting point, so a role loaded that way shows an unsaved change until saved.
  const [shareOffNote, setShareOffNote] = React.useState(false);
  const [whyOpen, setWhyOpen] = React.useState(false); // the info button's reason: hover, focus, or a click/tap
  const creationOk = meetsShareCreation(utils, anchorGrant);
  useEffect(() => {
    if (readOnly) return;
    if (shareSwitchesOff(switches, utils, anchorGrant)) {
      setSwitches((prev) => ({ ...prev, [SHARE]: 0 }));
      setShareOffNote(true);
    }
  }, [utils, anchorGrant, switches[SHARE], readOnly]);
  // The role's version (its updatedAt as loaded, then as last saved), sent as If-Match on Update so
  // the server refuses the save if someone else changed the role meanwhile. Kept outside the
  // unsaved-changes tracking: adopting a new version is never a change, and Reset leaves it alone.
  const versionRef = React.useRef(
    props.selectedRole && !props.isCloneRole ? props.selectedRole.updatedAt || null : null
  );
  const [versionConflict, setVersionConflict] = React.useState(false);
  const [pageOutdated, setPageOutdated] = React.useState(false);
  const [confirmLeave, setConfirmLeave] = React.useState(false);
  const snapshotOf = (s) => {
    const snap = JSON.parse(
      JSON.stringify({
        name: s.name,
        description: s.description,
        utils: s.utils,
        servicePermissions: s.servicePermissions,
        groupIds: s.groupIds,
        anchorGrant: s.anchorGrant,
        switches: s.switches,
      })
    );
    snap.permsTouched = !!s.permsTouched;
    snap.key = editorChangeKey(snap);
    return snap;
  };
  const baselineRef = React.useRef(null);
  const matrixSeededRef = React.useRef(false);
  if (baselineRef.current === null) {
    // First render: the solutions matrix is still empty; it is filled into the baseline
    // when the solutions load (below), which is the editor's own seed, not a user change.
    baselineRef.current = snapshotOf({
      name,
      description,
      utils,
      servicePermissions,
      groupIds,
      anchorGrant,
      switches,
      permsTouched: false,
    });
  }
  const currentStateKey = React.useMemo(
    () =>
      editorChangeKey({ name, description, utils, servicePermissions, groupIds, anchorGrant, switches }),
    [name, description, utils, servicePermissions, groupIds, anchorGrant, switches]
  );
  const isDirty = !readOnly && currentStateKey !== baselineRef.current.key;
  const resetToBaseline = () => {
    const b = JSON.parse(JSON.stringify(baselineRef.current));
    setName(b.name);
    setDescription(b.description);
    setUtils(b.utils);
    setServicePermissions(b.servicePermissions);
    setGroupIds(b.groupIds);
    setAnchorGrant(b.anchorGrant);
    setSwitches(b.switches);
    setShareOffNote(false);
    setWildReduceOK({ base: false, solution: false });
    setPendingWildDrop(null);
    setPendingChange(null);
    setAutoAssignedCells(new Set());
    setRemovedCells(new Set());
    setPermsTouched(b.permsTouched);
  };
  const handleBack = () => {
    if (isDirty) {
      setConfirmLeave(true);
      return;
    }
    props.handleEditClose();
  };
  // Authority ceiling for the '*' grant on each axis — the ORDINARY clamp: a caller may
  // grant grant-all only up to their OWN '*' rank (⊤/isUnscoped => Full). Above the
  // ceiling the control is read-only, which is exactly what reproduces "only a
  // super-admin may grant a super-admin" on the FE.
  const wildBaseCeiling = isUnscoped ? 2 : callerWildModuleRank;
  const wildSolutionCeiling = isUnscoped ? 2 : callerWildSolutionRank;
  // Change grant-all on one axis. ANY reduction below the LOADED rank (Full->Partial,
  // Full->Disabled, Partial->Disabled) is a deliberate lowering that opens the per-axis
  // confirmation dialog first — the only path that sets wildReduceOK[axis]. Raises and
  // no-ops apply directly.
  const setWild = (axis, nextRank) => {
    const loaded = loadedAnchorGrant[axis] || 0;
    if (nextRank < loaded && !wildReduceOK[axis]) {
      setPendingWildDrop({ axis, next: nextRank });
      return;
    }
    setAnchorGrant((prev) => ({ ...prev, [axis]: nextRank }));
    setPermsTouched(true);
  };

  useEffect(() => {
    if (!roleRes.isFetching && roleRes.isSuccess) {
      dispatch(setRole(roleRes.data.payload));
      setRoleChanged(false);
      // props.handleEditClose();
    }
  }, [roleRes.isFetching]);

  function patchGroupIds() {
    const tempGroupIds = [];
    props.selectedRole.services.forEach(s => {
      if (s.group && s.group.id) {
        tempGroupIds.push({ id: s.group.id, name: s.group.name, serviceId: s.serviceId })
      }
    })
    return tempGroupIds;
  }

  const resetGroupsToAssets = () => {
    setGroupIds([]);
  };

  useEffect(() => {
    // let tabs = {
    //   location: 'Tracking',
    //   maintenance: 'Rules',
    //   digitalTwin: 'Digital Twin',
    //   videoAnalytics: 'Video Analytics'
    // }
    // Seed the solutions matrix ONCE. A later refetch (for example after the sign-in token is
    // renewed, which changes this query's argument) would refill it from props.selectedRole, the
    // role as it was when the editor opened: that would wipe unsaved edits, and after an Update it
    // would put back pre-save values.
    if (!getServices.isFetching && getServices.isSuccess && !matrixSeededRef.current) {
      matrixSeededRef.current = true;
      // Per-solution matrix seed — the pure, round-trip-tested transform in
      // roleEditorSerde (create seeds ALL; edit reads the role doc; off-catalogue
      // assets excluded via the same allowlist). The caller-clamp/filter below is
      // create-only and stays here.
      const perms = loadServicesIntoPermissions(
        props.selectedRole || null,
        getServices.data.payload,
        allServices,
        assets,
        { createMode: !props.selectedRole }
      );
      // FE-3: on CREATE, a delegated (non-⊤) admin's solutions matrix is filtered to the
      // solutions they hold (metaData.services / eff) so a new role defaults to a grantable
      // set and out-of-scope solutions are never offered. EDIT/read-only is left UNFILTERED
      // (mirroring the create-only utilities clamp) so an existing role renders in full on
      // BOTH tabs — a non-holdable role is read-only anyway and the BE enforces bounds on
      // save. ⊤ and the eff-not-yet-loaded case fall through unfiltered (never blank the
      // matrix).
      const scopedPerms =
        isUnscoped || props.selectedRole
          ? perms
          : allServices && allServices.length
            ? clampServicesToCaller(
                perms.filter((p) =>
                  allServices.some((s) => String(s.id) === String(p.id))
                ),
                heldServiceTabs,
                callerWildSolutionRank
              )
            : perms;
      setServicePermissions(scopedPerms);
      // The loaded solutions matrix is the editor's seed, not a change: fold it into the
      // unsaved-changes baseline.
      baselineRef.current = snapshotOf({
        ...baselineRef.current,
        servicePermissions: scopedPerms,
      });
      // console.log({ perms })
    }
    if (!getServices.isFetching && getServices.isError) {
      showSnackbar("Role", getServices.data?.message, "error", 1000);
    }
  }, [getServices.isFetching]);

  function showSnackbar(title, message, variant, timeOut) {
    return enqueueSnackbar({ title, message, variant }, { timeOut });
  }

  async function addRole() {
    // Informational (non-blocking): solution-scoped module grants are inert on a role
    // that holds zero solutions (the BE rejects or returns nothing) — say so plainly.
    const heldRowsCount = servicePermissions.filter((r) =>
      (r.assets || []).some(
        (a) => a && !isScopingAsset(a.name) && permRank(a.permissions) >= 1
      )
    ).length;
    if (!heldRowsCount && !targetIsUnscoped) {
      const inert = ["Threshold Management", "Solution Edit", "Solution Creation"].filter(
        (t) =>
          utils.some((m) =>
            (m.tabs || []).some(
              (x) => x && x.name === t && permRank(x.permission) >= 1
            )
          )
      );
      if (inert.length) {
        showSnackbar(
          "Role",
          `${inert.join(", ")} granted with no solution held. These will have no effect until a solution is granted.`,
          "info",
          5000
        );
      }
    }
    // Item 5: pre-submit dependency net. Guarantees the submitted role is dependency-compliant
    // even for a cloned legacy role or state not set via the matrix handlers (the BE full-closure
    // gate 400s otherwise). Ungrantable required dep -> block; auto-assignable -> apply + ask to
    // review before re-saving. For state already closed by the handlers this is a no-op.
    // Only run the pre-submit closure when permissions were actually touched, or on
    // create/clone (whose seed must be validated). A pure name/description edit of a legacy
    // non-compliant role must not force a dependency re-save or block the rename.
    const runPreClose =
      !targetIsUnscoped &&
      (!props.selectedRole || props.isCloneRole || permsTouched);
    const preClose = runPreClose
      ? computeDependencyClosure({
          utils,
          servicePermissions,
          callerRankFor,
          // Both CREATE and EDIT close CHANGE-TRIGGERED off the open snapshot (for create
          // that snapshot IS the seed the matrix handlers already closed), so this submit
          // pass agrees with the interactive engine and never re-raises advisory deps the
          // admin deliberately turned off. Only CLONE imports foreign/legacy state that must
          // be fully re-validated, so it alone omits the baseline. Fixes the spurious
          // "Required dependencies (Controlling) were assigned automatically" false block on
          // create (was baseline=undefined => every edge re-fired against the all-ALL seed).
          baseline: props.isCloneRole ? undefined : seededStateRef.current || undefined,
        })
      : { utils, servicePermissions, autoAssigned: [] };
    if (runPreClose && !isUnscoped) {
      const bad = findUngrantableAutoAssign(preClose.autoAssigned, heldServiceTabs, isUnscoped, heldModuleTabs);
      if (bad) {
        showSnackbar("Role", bad, "error", 4000);
        return;
      }
    }
    // Auto-select policy: ONLY BE-ENFORCED dependency raises (which the save would 400/403 on
    // if absent) get applied + require an explicit review-and-resave (the explicit action).
    // Advisory raises are BE-legal, so they are NEVER applied silently at submit — they stay
    // a non-blocking greyed recommendation the admin opts into deliberately.
    const enforcedAuto = preClose.autoAssigned.filter((x) => x.enforced);
    if (enforcedAuto.length) {
      setServicePermissions(preClose.servicePermissions);
      setUtils(preClose.utils);
      setAutoAssignedCells((prev) => {
        const next = new Set(prev);
        preClose.autoAssigned.forEach((x) =>
          next.add(
            x.target === "serviceAsset"
              ? `svc|${x.serviceId}|${x.asset}`
              : `util|${x.module}|${x.tab}`
          )
        );
        return next;
      });
      const depNames = [
        ...new Set(enforcedAuto.map((x) => (x.target === "serviceAsset" ? x.asset : x.tab))),
      ];
      showSnackbar(
        "Role",
        `Required dependencies (${depNames.join(", ")}) were assigned automatically. Please review and save again.`,
        "info",
        4500
      );
      return;
    }
    setIsLoading(true);
    // What is being saved: becomes the unsaved-changes baseline if the Update succeeds.
    const savedSnapshot = snapshotOf({
      name,
      description,
      utils,
      servicePermissions,
      groupIds,
      anchorGrant,
      switches,
      permsTouched,
    });

    // Serialize via the pure, round-trip-tested serde. It emits the enumerated
    // module/service rows AND the '*' grant-all rows (from anchorGrant); a lowering of
    // grant-all below its loaded value is honoured only with allowDropWildcard (the
    // removal dialog), and signals body.dropWildcard so the BE's LD-3 guard permits it.
    let body = serializeEditorState({
      name,
      description,
      utils,
      servicePermissions,
      anchorGrant,
      loadedAnchorGrant,
      groupIds,
      allowDropWildcard: wildReduceOK,
      switches,
    });
    if (!props.selectedRole || props.isCloneRole) {
      body.name=removeExtraSpaces(body.name)
      let createdRole = await createRole({ token, body });
      if (createdRole.data?.success) {
        showSnackbar("Role", createdRole.data?.message, "success", 1000);
        props.handleEditClose();
        setIsLoading(false);
      }
      if (createdRole.error) {
        showSnackbar(
          "Role",
          createdRole.data?.message || createdRole?.error?.data?.message,
          "error",
          4000
        );
        setIsLoading(false);
      }
    } else {
      // Compare with the last SAVED name (the baseline), not the name the editor opened with:
      // after a renaming Update, renaming back must still be sent.
      if (name == baselineRef.current.name) {
        delete body.name;
      }
      let updatedRole = await updateRole({
        token,
        body,
        id: props.selectedRole._id,
        ifMatch: versionRef.current,
      });
      if (updatedRole.data?.success) {
        // Adopt the stored role's new version for the next save (the editor stays open).
        if (updatedRole.data?.payload?.updatedAt) versionRef.current = updatedRole.data.payload.updatedAt;
        setRoleChanged(true);
        // The editor stays OPEN after an Update — re-baseline the anchor round-trip to the
        // just-persisted grant-all so a further reduction is measured against the new
        // stored value, and re-arm the per-axis reduction confirmation.
        setLoadedAnchorGrant(anchorGrant);
        setWildReduceOK({ base: false, solution: false });
        // The saved role is the new starting point: Reset returns to it, and Update and
        // Reset grey out again until the next change.
        baselineRef.current = savedSnapshot;
        setShareOffNote(false);
        showSnackbar("Role", updatedRole.data?.message, "success", 1000);
        setIsLoading(false);
      }
      if (updatedRole.error) {
        const err = (updatedRole.error && updatedRole.error.data) || {};
        // A refused last-admin change: the server restored the permissions but kept the name and
        // description that were sent, and answers with the role's new version. Adopt both, so the
        // corrected re-save is neither a false conflict nor a "name duplicated" refusal. Done before
        // setIsLoading, which re-renders at once (React 17), so Update and Reset reflect it.
        if (updatedRole.error.status === 409 && err.updatedAt && err.errorCode !== "ROLE_VERSION_CONFLICT") {
          versionRef.current = err.updatedAt;
          baselineRef.current = snapshotOf({ ...baselineRef.current, name, description });
        }
        setIsLoading(false);
        // Someone else changed the role since it was opened: nothing was saved; Reload shows it.
        if (err.errorCode === "ROLE_VERSION_CONFLICT") {
          setVersionConflict(true);
          return;
        }
        // The server requires the version stamp and this page did not send one (should not happen
        // with this editor; kept for safety): ask for a page reload.
        if (err.errorCode === "ROLE_VERSION_REQUIRED") {
          setPageOutdated(true);
          return;
        }
        showSnackbar(
          "Role",
          updatedRole.data?.message || updatedRole?.error?.data?.message,
          "error",
          4000
        );
      }
    }
  }

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  // ---- Adaptive impact pipeline (top-down model) ----
  // intended = the user's gesture, capped to their own authority (clamp).
  // final    = intended + dependency chain auto-grants (closure, e.g. Thresholds pulls
  //            Controlling and its utility part) + master-layer cascade (gated cells
  //            mirrored down to the utility rank, orphaned sources deselected).
  // Side effects beyond the gesture open an impact-preview dialog before anything is
  // applied; a clean single-cell change applies instantly. Highlights: amber = auto
  // granted, red tint = auto deselected or lowered.
  const keyOf = (x) =>
    x.target === "utilityTab"
      ? `util|${x.module}|${x.tab}`
      : `svc|${x.serviceId}|${x.asset}`;
  const commitChange = (final, added, removed, touchedKeys) => {
    setUtils(final.utils);
    setServicePermissions(final.servicePermissions);
    // Tint lifecycle: a cell the USER just set is a manual state — stale
    // automation tints on it are cleared; a cell newly auto-granted loses any
    // old red tint and vice versa (amber and red are mutually exclusive, the
    // LATEST automation wins). Without this, two solutions in the same final
    // state could show different highlights purely from gesture history.
    const addedKeys = new Set(added.map(keyOf));
    const removedKeys = new Set(removed.map(keyOf));
    const touched = new Set(touchedKeys || []);
    setAutoAssignedCells((prev) => {
      const next = new Set(prev);
      touched.forEach((k) => next.delete(k));
      removedKeys.forEach((k) => next.delete(k));
      addedKeys.forEach((k) => next.add(k));
      return next;
    });
    setRemovedCells((prev) => {
      const next = new Set(prev);
      touched.forEach((k) => next.delete(k));
      addedKeys.forEach((k) => next.delete(k));
      removedKeys.forEach((k) => next.add(k));
      return next;
    });
    if (added.length || removed.length) {
      const parts = [];
      if (added.length) parts.push(`granted ${added.length} required permission(s)`);
      if (removed.length)
        parts.push(`deselected or lowered ${removed.length} dependent permission(s)`);
      showSnackbar(
        "Role",
        `Also ${parts.join(" and ")}. Highlighted for review.`,
        "info",
        3500
      );
    }
  };

  // Compact, scale-friendly lines for the impact preview: base-permission entries
  // grouped by module and level change (a master lowering that mirrors a whole
  // group reads as one line), solution entries grouped by asset and level change
  // with a short solution sample.
  const summarizeImpact = (list) => {
    const lines = [];
    const utilGroups = {};
    const svcGroups = {};
    for (const x of list) {
      if (x.target === "utilityTab") {
        const k = `${x.module}|${x.from}|${x.to}`;
        (utilGroups[k] = utilGroups[k] || { ...x, tabs: [] }).tabs.push(x.tab);
      } else {
        const k = `${x.asset}|${x.from}|${x.to}`;
        (svcGroups[k] = svcGroups[k] || { ...x, names: [] }).names.push(
          x.serviceName || String(x.serviceId)
        );
      }
    }
    for (const g of Object.values(utilGroups)) {
      if (g.tabs.length > 2) {
        const shown = g.tabs.slice(0, 3).join(", ");
        const more = g.tabs.length > 3 ? ` and ${g.tabs.length - 3} more` : "";
        lines.push(
          `${g.module}: ${g.tabs.length} permissions ${permLabel(g.from)} to ${permLabel(g.to)} (${shown}${more})`
        );
      } else {
        for (const tab of g.tabs) {
          lines.push(
            `${tab} under ${g.module}: ${permLabel(g.from)} to ${permLabel(g.to)}`
          );
        }
      }
    }
    for (const g of Object.values(svcGroups)) {
      const shown = g.names.slice(0, 3).join(", ");
      const more = g.names.length > 3 ? ` and ${g.names.length - 3} more` : "";
      lines.push(
        `${g.asset} on ${g.names.length} solution${g.names.length > 1 ? "s" : ""}: ${permLabel(g.from)} to ${permLabel(g.to)} (${shown}${more})`
      );
    }
    return lines;
  };

  const applyMatrixChange = (nextUtils, nextServices, touchedKeys) => {
    // Superadmin target role: gates and dependencies are meaningless against it (it holds
    // everything implicitly; the BE exempts it) — apply the gesture as-is, no side effects.
    if (targetIsUnscoped) {
      setUtils(nextUtils);
      setServicePermissions(nextServices);
      return;
    }
    // 1. The gesture, capped to the caller's own authority.
    let intended = { utils: nextUtils, servicePermissions: nextServices };
    if (scopedEditor) {
      intended = clampPermsToCallerHoldings(
        intended.utils,
        intended.servicePermissions,
        heldModuleTabs,
        heldServiceTabs,
        callerWildModuleRank,
        callerWildSolutionRank
      );
    }
    // 2. Dependency chain auto-grants — CHANGE-TRIGGERED: edges fire only for sources this
    // gesture raised above the pre-gesture state, so a decreasing or unrelated gesture can
    // never escalate anything (latent gaps in legal legacy states are left alone).
    const closure = computeDependencyClosure({
      utils: intended.utils,
      servicePermissions: intended.servicePermissions,
      callerRankFor,
      baseline: { utils, servicePermissions },
    });
    if (!isUnscoped) {
      const bad = findUngrantableAutoAssign(
        closure.autoAssigned,
        heldServiceTabs,
        isUnscoped,
        heldModuleTabs
      );
      if (bad) {
        showSnackbar("Role", bad, "error", 4000);
        return;
      }
    }
    // 3. Master-layer cascade: gated cells mirror the utility rank; orphaned sources drop.
    const cascaded = cascadeGates({
      utils: closure.utils,
      servicePermissions: closure.servicePermissions,
    });
    const final = {
      utils: cascaded.utils,
      servicePermissions: cascaded.servicePermissions,
    };
    // 4. Side effects = every change beyond the intended gesture.
    const side = diffPerms(intended, final);
    if (!side.length) {
      commitChange(final, [], [], touchedKeys);
      return;
    }
    const added = side.filter((x) => permRank(x.to) > permRank(x.from));
    const removed = side.filter((x) => permRank(x.to) < permRank(x.from));
    // 5. Multi-cell impact: preview before applying (Proceed applies, Cancel discards).
    setPendingChange({ final, added, removed, touchedKeys });
  };

  const servicePermissionChange = (service, asset, value, overall) => {
    if (readOnly) return; // view-only: never mutate (matrices are inert too)
    setPermsTouched(true);
    let perms = JSON.parse(JSON.stringify(servicePermissions));
    // The row Overall is an OPERATIONAL summary: scoping entries (Scheduled
    // Reports / Threshold Profiles) are excluded from it, and a bulk Full/Partial
    // never touches them (they scope capabilities and are granted deliberately).
    // Row Overall = Disabled is the remove-this-solution gesture and clears
    // EVERYTHING, scoping included.
    const opAssets = (row) =>
      row.assets.filter((a) => a && !isScopingAsset(a.name));
    const rowIsHeld = (row) =>
      (row.assets || []).some(
        (a) => a && !isScopingAsset(a.name) && permRank(a.permissions) >= 1
      );
    // Scoping columns scope the row's FEATURES: they cannot be the only grant on
    // a solution. A raise on a feature-less row is refused (the BE rejects the
    // shape too); clearing is always allowed.
    if (isScopingAsset(asset) && !overall && permRank(value) >= 1) {
      const targets = service == -1 ? perms : [perms[service]];
      const allFeatureless = targets.every((r) => r && !rowIsHeld(r));
      if (allFeatureless) {
        showSnackbar(
          "Role",
          `"${asset}" scopes this solution's features - grant a feature permission on the solution first.`,
          "error",
          4000
        );
        return;
      }
    }
    if (!overall) {
      if (service == -1) {
        //make all the rows
        const servicesLength = servicePermissions.length;
        for (let i = 0; i < servicesLength; i++) {
          // Guard: conditional columns (Rules / Tracking / Digital Twin / Video
          // Analytics) are absent on some rows — an unguarded .find(...).permissions crashed.
          const cell = perms[i].assets.find((a) => a.name == asset);
          // A bulk raise on a scoping column skips feature-less rows entirely.
          if (cell && !(isScopingAsset(asset) && permRank(value) >= 1 && !rowIsHeld(perms[i]))) {
            cell.permissions = value;
          }
          if (!opAssets(perms[i]).filter((a) => a.permissions != value).length) {
            perms[i].overall = value;
          } else {
            perms[i].overall = "";
          }
        }
      } else {
        const cell = perms[service].assets.find((a) => a.name == asset);
        if (cell) cell.permissions = value;
        if (
          !opAssets(perms[service]).filter((a) => a.permissions != value).length
        ) {
          perms[service].overall = value;
        } else {
          perms[service].overall = "";
        }
      }
    } else {
      if (service == -1) {
        const servicesLength = servicePermissions.length;
        for (let i = 0; i < servicesLength; i++) {
          perms[i].overall = value;
          perms[i].assets.forEach((a) => {
            if (value === "disable" || !isScopingAsset(a.name))
              a.permissions = value;
          });
        }
      } else {
        perms[service].overall = value;
        perms[service].assets.forEach((a) => {
          if (value === "disable" || !isScopingAsset(a.name))
            a.permissions = value;
        });
      }
    }
    // Item 5: block a downgrade that orphans an enforced dependency — lowering Controlling
    // below what a held "Threshold Management" requires, on a row that STAYS held (clearing
    // a row's last tab is allowed). Evaluated on the post-mutation state.
    if (!overall && isEnforcedDepAsset(asset)) {
      const need = requiredEnforcedDepLevel(utils, asset);
      if (need && permRank(value) < need) {
        const rows = service == -1 ? perms : [perms[service]];
        const orphaned = (rows || []).some(
          (r) =>
            r &&
            (r.assets || []).some((a) => a && permRank(a.permissions) >= 1) &&
            (r.assets || []).some(
              (a) => a && a.name == asset && permRank(a.permissions) < need
            )
        );
        if (orphaned) {
          showSnackbar(
            "Role",
            `"${asset}" is required by "Threshold Management" on solutions this role holds. Disable "Threshold Management" first.`,
            "error",
            4000
          );
          return;
        }
      }
    }
    // Keys the USER's gesture directly set (their automation tints clear).
    const svcTouched = [];
    if (!overall) {
      const targets = service == -1 ? perms : [perms[service]];
      targets.forEach((r) => {
        if (r && (r.assets || []).some((a) => a && a.name == asset))
          svcTouched.push(`svc|${r.serviceId}|${asset}`);
      });
    } else {
      const targets = service == -1 ? perms : [perms[service]];
      targets.forEach((r) => {
        if (!r) return;
        (r.assets || []).forEach((a) => {
          if (a && a.name) svcTouched.push(`svc|${r.serviceId}|${a.name}`);
        });
      });
    }
    // Item 5 + first-layer rule: clamp, close over dependencies, gate, apply.
    applyMatrixChange(utils, perms, svcTouched);
  };
  const utilPermissionChange = (util, tab, value, overall) => {
    if (readOnly) return; // view-only: never mutate (matrices are inert too)
    setPermsTouched(true);
    let tempUtils = JSON.parse(JSON.stringify(utils));
    if (!overall) {
      tempUtils[util].tabs[tab].permission = value;
      if (
        !tempUtils[util].tabs.filter(
          (t) => t.permission && t.permission != value
        ).length
      ) {
        tempUtils[util].overall = value;
      } else {
        tempUtils[util].overall = "";
      }
    } else {
      tempUtils[util].overall = value;
      tempUtils[util].tabs.forEach((t) => {
        t.permission = value;
      });
    }
    const utilTouched = !overall
      ? [`util|${tempUtils[util].name}|${tempUtils[util].tabs[tab].name}`]
      : tempUtils[util].tabs
          .filter((t) => t && t.name)
          .map((t) => `util|${tempUtils[util].name}|${t.name}`);
    // Item 5 + first-layer rule: granting "Threshold Management" auto-assigns Controlling on
    // held solution rows, which in turn pulls its utility part; lowering a utility part below
    // what the solution rows use is self-healed by the closure (re-raised + highlighted).
    applyMatrixChange(tempUtils, servicePermissions, utilTouched);
  };

  const handleGroupClick = (id) => {
    if (readOnly) return; // view-only: the group narrowing modal is a write surface
    setServiceId(id)
    setGroupModal(!groupModal);
    // console.log(id, props.selectedRole)
  };

  const AccessType = ({ asset }) => {
    // Master access level for a whole COLUMN across all solutions.
    //  - "Overall Permissions" -> every feature column on every solution (overall)
    //  - a feature/scoping asset -> that one column across every solution
    const setLevel = (lvl) =>
      asset === "Overall Permissions"
        ? servicePermissionChange(-1, null, lvl, true)
        : servicePermissionChange(-1, asset, lvl, false);
    // D26 (a): Full only when every cell this master sets can take Full for the editor. It
    // sets every row, including rows the solution search hides, so all rows are checked.
    const fullBlocked =
      scopedEditor &&
      bulkFullBlocked(
        servicePermissions,
        asset === "Overall Permissions" ? null : asset,
        callerRankFor
      );
    return (
      <div
        style={{
          display: "flex",
          gap: 4,
          justifyContent: "center",
        }}
      >
        {fullBlocked ? (
          HIDDEN_LEVEL_SLOT
        ) : (
          <Tooltip title="Full Access">
            <CheckCircleOutlinedIcon
              onClick={() => setLevel("ALL")}
              sx={{ color: "#222", cursor: "pointer" }}
            />
          </Tooltip>
        )}
        <Tooltip title="Partial Access">
          <VisibilityOutlinedIcon
            onClick={() => setLevel("READ")}
            sx={{ color: "#222", cursor: "pointer" }}
          />
        </Tooltip>
        <Tooltip title="Disabled">
          <CancelOutlinedIcon
            onClick={() => setLevel("disable")}
            sx={{ color: "#222", cursor: "pointer" }}
          />
        </Tooltip>
      </div>
    );
  };
  // Perf: reveal a column-header master on CSS :hover instead of a React hover
  // state. dispatchHoverState previously re-rendered the ENTIRE per-solution
  // matrix (up to ~130 rows x ~15 cols) on every mouse enter/leave, which is the
  // "going over the columns lags" report. CSS hover toggles label<->master with
  // zero re-renders. The master is always in the DOM (hidden) so its onClick works.
  const masterHeaderSx = {
    "& .mh-master": { display: "none" },
    "&:hover .mh-master": { display: "flex" },
    "&:hover .mh-label": { display: "none" },
  };
  // Anchor round-trip (Step 2): the pinned "Grant all" control shown above each matrix.
  // '*' is now an ordinary, first-class editor value — a floor over every row (current
  // AND future), clamped to the caller's own '*' rank. Rendered as a band above the
  // catalogue rows (not a TableRow — keeps the matrix column arithmetic untouched).
  const renderGrantAll = (axis, ceiling, label, caption) => {
    const current = anchorGrant[axis] || 0;
    // Show only when it is actionable (caller can grant it) OR the target already holds
    // it (so it is visible and removable) — otherwise it is noise.
    if (ceiling < 1 && current < 1) return null;
    // No SELF-RAISE: a role editing its OWN grant-all may keep or lower it, but NEVER raise
    // it — grant-all elevation must come from a higher-privileged admin or the master roles
    // API (the BE enforces this too via canGrant + the self-raise guard). Cap at the LIVE
    // current value (not the open-time loaded value): once you lower your own grant-all in
    // this session, you cannot raise it back either — only a higher role / master API can.
    // Using `current` (vs a possibly-stale loaded/ceiling snapshot) makes the block robust.
    const selfLimit = isOwnRole ? current : Infinity;
    const opts = [
      { r: 0, l: "Disabled" },
      { r: 1, l: "Partial Access" },
      { r: 2, l: "Full Access" },
    ];
    return (
      <Paper
        variant="outlined"
        sx={{ p: 1.5, mb: 1.5, borderColor: "primary.light", bgcolor: "action.hover" }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
          <Box>
            <Typography sx={{ fontWeight: 600 }}>{label}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
              {caption}
            </Typography>
            {current >= 1 && (
              <Typography variant="caption" color="primary" sx={{ display: "block", mt: 0.5 }}>
                {current >= 2 ? (
                  axis === "base" ? (
                    <>
                      Every module is granted at Full Access via Grant all - the rows below are
                      inherited and shown for reference (dimmed). Share Solutions and Receive Shared
                      Solutions are not part of Grant all and stay settable.
                    </>
                  ) : (
                    <>
                      Every solution is granted at Full Access via Grant all - feature access on the
                      rows below is inherited. Groups remain configurable.
                    </>
                  )
                ) : (
                  <>
                    Every {axis === "base" ? "module" : "solution"} is granted at Partial Access via
                    Grant all; grant individual rows below for more (rows are additive).
                  </>
                )}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            {opts.map((o) => {
              const overCeiling = o.r > ceiling;
              const selfBlocked = o.r > selfLimit; // can't raise your own grant-all
              const selected = current === o.r;
              // D26 (a): a level above the editor's own grant-all is hidden, not greyed; the
              // role's current level always shows (e.g. on a clone of a broader role).
              if (overCeiling && !selected) return null;
              const tip = overCeiling
                ? "Limited to your own access level"
                : selfBlocked
                  ? "You cannot raise your own grant-all - a higher-privileged admin or the master roles API must do it"
                  : "";
              return (
                <Tooltip key={o.r} title={tip}>
                  <span>
                    <Button
                      size="small"
                      variant={selected ? "contained" : "outlined"}
                      disabled={readOnly || overCeiling || selfBlocked}
                      onClick={() => setWild(axis, o.r)}
                    >
                      {o.l}
                    </Button>
                  </span>
                </Tooltip>
              );
            })}
          </Box>
        </Box>
      </Paper>
    );
  };
  // Solution sharing switches (plan stage 6, owner decision S6-1 B): two more cells in the Solution
  // Management row, right after its own tabs. Their values live in `switches`, not in the matrix state, so the
  // matrix rules (caller clamps, hidden tabs, the row's Overall control, the parent cap, the
  // grant-all fold) never reach them, and the cells stay clickable on the inert table of a
  // grant-all role. A level this editor may not set is hidden (D26 a); the role's current and
  // stored levels always show, so keeping, lowering and Off are always offered.
  const SWITCH_TEXT = {
    [SHARE]:
      "People in this role may share the solutions they created with roles that accept shared solutions. Full also lets this role switch Share on, at Partial, for roles it manages. Needs Solution Creation at Full Access; switched off when it is lowered.",
    [RECEIVE]:
      "This role accepts solutions shared with it, at its own usual levels. Full also lets this role switch Receive on, at Partial, for roles it manages. Set on its own: the row's Overall setting does not change it.",
  };
  const SWITCH_FULL_NOTE =
    "Only roles that also hold this at Full, and grant-all admins, will be able to manage this role and its users. On an existing role, its administrators need it at Full first. Giving it to one of two identical roles puts that role above the other.";
  // Only the first Solution Management row carries the cells (a stored role may repeat the row;
  // the switch levels are read across all of them).
  const switchRowIndex = utils.findIndex((u) => u.name === SWITCH_MODULE);
  const switchCellCount = (utilIndex) => (utilIndex === switchRowIndex ? SWITCHES.length : 0);
  const renderSwitchCell = (tab) => {
    const current = switches[tab] || 0;
    // Offered: up to the STORED level (the starting point: as loaded, saved, restored or
    // reloaded) or what this editor may raise to, whichever is higher - the server judges
    // rises against the stored role, so going back to it after lowering is not a raise.
    const stored = ((baselineRef.current && baselineRef.current.switches) || {})[tab] || 0;
    const allowed = switchOptions({ current, stored, tab, ...switchCtx });
    const which = tab === SHARE ? "share" : "receive";
    // O23: Share's offered Partial and Full are greyed (shown, not hidden) while the role lacks
    // Solution Creation at Full; levels this editor may not set stay hidden (D26 a).
    const blocked = (level) => tab === SHARE && level >= 1 && !creationOk;
    const LEVEL_NAME = ["Off", "Partial Access", "Full Access"];
    const levelControl = (level, title, SelectedIcon, selectedColor, OpenIcon) =>
      allowed.indexOf(level) === -1 ? (
        HIDDEN_LEVEL_SLOT
      ) : (
        <Tooltip title={blocked(level) ? SHARE_NEEDS_CREATION_HINT : title} describeChild>
          <span
            aria-label={`${tab}: ${LEVEL_NAME[level]}${blocked(level) ? `. ${SHARE_NEEDS_CREATION_HINT}` : ""}`}
            data-testid={`switch-${which}-${level}`}
            data-selected={current === level ? "true" : "false"}
            data-disabled={blocked(level) ? "true" : "false"}
            aria-disabled={blocked(level) ? "true" : undefined}
            style={{
              display: "inline-flex",
              cursor: readOnly || current === level || blocked(level) ? "default" : "pointer",
            }}
            onClick={() => {
              if (readOnly || current === level || blocked(level)) return;
              setSwitches((prev) => ({ ...prev, [tab]: level }));
              if (tab === SHARE && level >= 1) setShareOffNote(false);
            }}
          >
            {current === level ? (
              <SelectedIcon color={blocked(level) ? "disabled" : selectedColor} />
            ) : (
              <OpenIcon sx={{ color: blocked(level) ? "#c4c4c4" : "#666" }} />
            )}
          </span>
        </Tooltip>
      );
    // Hover is not enough for keyboards and touch screens: the reason also sits behind a focusable
    // info button (the pattern of the share table's greyed rows).
    const showWhy = !readOnly && tab === SHARE && !creationOk && (allowed.indexOf(1) !== -1 || allowed.indexOf(2) !== -1);
    // Always open to the pointer, also on the inert table of a grant-all or read-only role, so the
    // explanations show on hover; a read-only editor's clicks change nothing (the handler checks).
    return (
      <TableCell key={tab} align="center" className="sharing-switch" sx={{ pointerEvents: "auto" }}>
        <Tooltip title={SWITCH_TEXT[tab]}>
          <div style={{ fontSize: 12, color: "#444" }}>{tab}</div>
        </Tooltip>
        {showWhy && (
          <Tooltip
            title={SHARE_NEEDS_CREATION_HINT}
            describeChild
            open={whyOpen}
            onOpen={() => setWhyOpen(true)}
            onClose={() => setWhyOpen(false)}
          >
            <IconButton
              size="small"
              aria-label="Why can't Share Solutions be switched on?"
              data-testid="share-needs-creation-info"
              onClick={() => setWhyOpen(true)} // a tap or click opens it; leaving, blur or the touch timeout close it
              sx={{ p: 0.25 }}
            >
              <InfoOutlinedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
        <div style={{ display: "flex", gap: 4, justifyContent: "center" }}>
          {levelControl(2, SWITCH_FULL_NOTE, CheckCircleIcon, "success", CheckCircleOutlinedIcon)}
          {levelControl(1, "Partial Access (can use)", VisibilityIcon, "warning", VisibilityOutlinedIcon)}
          {levelControl(0, "Off", CancelIcon, "error", CancelOutlinedIcon)}
        </div>
      </TableCell>
    );
  };
  const renderSharingNote = () => {
    const sentences = [];
    if (!sharingOn)
      sentences.push(
        "Sharing is not switched on yet, so they can be kept, lowered or switched off, but not switched on."
      );
    if (cloneLoweredSwitches) sentences.push("Lowered on this copy to what you can grant.");
    return sentences.length ? (
      <Typography sx={{ fontSize: 12, color: "#666", mt: 1 }} data-testid="solution-sharing-note">
        {`${SHARE} and ${RECEIVE} (Solution Management row): ${sentences.join(" ")}`}
      </Typography>
    ) : null;
  };
  // wildFloor (Step 2 follow-up — the "deferred wildFloor follow-up" the Solutions matrix
  // note below refers to): the '*' grant-all rank is a MINIMUM every row inherits, so show
  // each row at max(own, floor) and lock the sub-floor levels — a row may be RAISED above the
  // floor (additive) but never shown/set below what '*' already grants (the wildcard confers
  // it regardless of parent gates). DISPLAY-ONLY: utils/servicePermissions are UNTOUCHED, so
  // serializeEditorState still emits only the '*' rows and a pure-'*' role round-trips with no
  // enumerated rows persisted. Base axis: '*' module @rank grants EVERY module tab at that rank
  // (hasModulePermission maxes over '*'/'*'), so anchorGrant.base folds onto every module row +
  // tab. Solution axis: '*' service grants exactly the ROLE_ASSETS feature set (mirrors the BE
  // GRANT_ALL_SERVICE_TABS) + the feature Overall - NOT the Groups chip (a distinct axis '*' does
  // not subsume), which is a separate cell left untouched.
  const wildDispPerm = (ownPerm, floor) => {
    const f = floor || 0;
    // No '*' on this axis => EXACT passthrough. Critical: preserve the caller's own value
    // verbatim (incl. the "" mixed-Overall sentinel and an undefined/absent asset cell), so
    // the common no-wildcard path is byte-for-byte unchanged — a mixed row must NOT collapse
    // to a filled "disable" (permRank("") === 0 would otherwise read as Disabled).
    if (f === 0) return ownPerm;
    // Floor active: an indeterminate/mixed Overall ("") stays mixed until the floor forces
    // EVERY tab to one rank — i.e. only Full (f>=2) makes it uniformly ALL; at Partial it is
    // at least READ but may hold higher own tabs, so keep it mixed.
    if (ownPerm === "" || ownPerm == null) return f >= 2 ? "ALL" : "";
    const r = Math.max(permRank(ownPerm), f);
    return r >= 2 ? "ALL" : r >= 1 ? "READ" : "disable";
  };
  const WILD_BASE_TIP =
    "Granted via “Grant all modules” — lower Grant all to set this row individually";
  const WILD_SOL_TIP =
    "Granted via “Grant all solutions” — lower Grant all to set this row individually";
  // Item 2 greying: the tightest R1/R7 gate rank across a module's tabs, so the module-row
  // Overall master greys in step with its capped tabs (disabling Administration > Solution
  // Management now greys the Solution Management + Solution Edit Overall, not just the tabs).
  // Infinity = the module has no gated tab (uncapped). Anchor target skips all gating.
  const moduleOverallGateRank = (util) =>
    targetIsUnscoped
      ? Infinity
      : Math.min(
          Infinity,
          ...((util.tabs || []).map((t) =>
            t && t.name
              ? Math.min(
                  moduleTabGateRank(utils, util.name, t.name),
                  // item 1: base Analytics surfaces are capped by the Analytics feature, so
                  // the Analytics module Overall greys when the feature drops.
                  moduleTabSurfaceGateRank(utils, util.name, t.name)
                )
              : Infinity
          ))
        );

  // Item 5b/7 (visual hide): render views. A scoped (non superadmin) editor sees ONLY the
  // module rows/tabs, solution rows and asset columns they hold. CRITICAL: the state atoms
  // stay COMPLETE and the views carry ORIGINAL indexes into them, so the handlers (which
  // address by index) and the submit path are untouched by the filtering.
  const visibleTabEntries = (util) => {
    const entries = (util.tabs || [])
      .map((t, j) => ({ t, j }))
      .filter(({ t }) => t && t.name);
    return scopedEditor
      ? entries.filter(({ t }) => callerModuleRankFor(util.name, t.name) >= 1)
      : entries;
  };
  // The Solution Management row always shows: it carries the sharing switches (S6-1 B), which an
  // editor of a role keeps, lowers or removes even without holding a Solution Management tab.
  const visibleUtils = utils
    .map((u, i) => ({ u, i }))
    .filter(
      ({ u, i }) => !scopedEditor || visibleTabEntries(u).length > 0 || switchCellCount(i) > 0
    );
  // Solutions ordering: most granted permissions first, then alphabetical. Captured ONCE
  // from the seeded grants (a ref) so rows do not jump around while the user edits cells.
  // Snapshot of the state AT EDITOR OPEN: the submit-time closure baseline, so a session
  // that only lowered permissions never re-raises legacy gaps at save time.
  const seededStateRef = React.useRef(null);
  if (seededStateRef.current === null && servicePermissions.length) {
    seededStateRef.current = JSON.parse(
      JSON.stringify({ utils, servicePermissions })
    );
  }
  const initialOrderRef = React.useRef(null);
  if (initialOrderRef.current === null && servicePermissions.length) {
    initialOrderRef.current = servicePermissions
      .map((s) => ({
        id: String(s.serviceId),
        granted: (s.assets || []).filter((a) => a && permRank(a.permissions) >= 1)
          .length,
        name: s.name || "",
      }))
      .sort((a, b) => b.granted - a.granted || a.name.localeCompare(b.name))
      .map((x) => x.id);
  }
  const _orderIdx = (sid) => {
    const arr = initialOrderRef.current || [];
    const i = arr.indexOf(String(sid));
    return i === -1 ? arr.length : i;
  };
  const visibleServices = servicePermissions
    .map((s, i) => ({ s, i }))
    .filter(
      ({ s }) =>
        !scopedEditor ||
        Object.values(heldServiceTabs[s.serviceId] || {}).some((r) => r >= 1)
    )
    .filter(
      ({ s }) =>
        !solutionSearch ||
        (s.name || "").toLowerCase().includes(solutionSearch.toLowerCase())
    )
    .sort((a, b) => _orderIdx(a.s.serviceId) - _orderIdx(b.s.serviceId));
  const visibleAssets = scopedEditor
    ? assets.filter((a) =>
        visibleServices.some(({ s }) => callerRankFor(s.serviceId, a) >= 1)
      )
    : assets;
  // P2b: modules have varying tab counts (Solution Management has 11 part-tabs vs 4-5 on
  // others), so the header colspan + per-row tab-cell count derive from the max VISIBLE count
  // (the Solution Management row counts its two sharing switch cells too).
  const maxTabColumns = Math.max(
    4,
    ...visibleUtils.map(({ u, i }) => visibleTabEntries(u).length + switchCellCount(i))
  );

  if (callerPredatesTaxonomy) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography sx={{ fontSize: 15, color: "#555" }}>
          Permissions upgrade in progress. Role editing resumes once your
          account's permissions are migrated to the new structure. Please try
          again shortly or contact your administrator.
        </Typography>
      </Box>
    );
  }
  return (
    <Fragment>
      {/* Impact preview: a gesture whose dependency effects reach beyond the clicked
          cell is applied only after the admin reviews and confirms it here. */}
      <Dialog
        open={!!pendingChange}
        onClose={() => setPendingChange(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Review dependent changes</DialogTitle>
        <DialogContent>
          {pendingChange && pendingChange.added.length > 0 && (
            <Fragment>
              <div style={{ fontWeight: 600, margin: "4px 0" }}>
                This change will also grant
              </div>
              {summarizeImpact(pendingChange.added).map((line, i) => (
                <div key={`a${i}`} style={{ fontSize: 14, margin: "2px 0" }}>
                  {line}
                </div>
              ))}
            </Fragment>
          )}
          {pendingChange && pendingChange.removed.length > 0 && (
            <Fragment>
              <div style={{ fontWeight: 600, margin: "10px 0 4px" }}>
                This change will also deselect or lower
              </div>
              {summarizeImpact(pendingChange.removed).map((line, i) => (
                <div key={`r${i}`} style={{ fontSize: 14, margin: "2px 0" }}>
                  {line}
                </div>
              ))}
            </Fragment>
          )}
          {pendingChange && shareSwitchesOff(switches, pendingChange.final.utils, anchorGrant) && (
            <div style={{ fontSize: 14, margin: "10px 0 2px" }} data-testid="preview-share-off">
              Share Solutions: switched off (it needs Solution Creation at Full Access)
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingChange(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => {
              commitChange(
                pendingChange.final,
                pendingChange.added,
                pendingChange.removed,
                pendingChange.touchedKeys
              );
              setPendingChange(null);
            }}
          >
            Proceed
          </Button>
        </DialogActions>
      </Dialog>
      {/* Grant-all reduction confirmation — the ONLY path that sets wildReduceOK[axis], so
          a serializer path can never silently reduce or destroy a role's grant-all. Scoped
          per-axis so confirming one axis never waves through the other. */}
      <Dialog open={!!pendingWildDrop} onClose={() => setPendingWildDrop(null)}>
        <DialogTitle>
          {pendingWildDrop && pendingWildDrop.next < 1 ? "Remove grant-all?" : "Reduce grant-all?"}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {pendingWildDrop && pendingWildDrop.next < 1 ? (
              <>
                Removing grant-all will drop this role's access to every{" "}
                {pendingWildDrop.axis === "base" ? "module" : "solution"} not listed below,
                including future ones. This cannot be undone from this screen.
              </>
            ) : (
              <>
                Reducing grant-all to Partial Access will lower this role's access on every{" "}
                {pendingWildDrop && pendingWildDrop.axis === "base" ? "module" : "solution"},
                including future ones, except where a row below grants more.
              </>
            )}
          </Typography>
          {pendingWildDrop &&
            shareSwitchesOff(switches, utils, { ...anchorGrant, [pendingWildDrop.axis]: pendingWildDrop.next }) && (
              <Typography sx={{ mt: 1 }} data-testid="wild-drop-share-off">
                Share Solutions: switched off (it needs Solution Creation at Full Access)
              </Typography>
            )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingWildDrop(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              const { axis, next } = pendingWildDrop;
              setWildReduceOK((prev) => ({ ...prev, [axis]: true }));
              setAnchorGrant((prev) => ({ ...prev, [axis]: next }));
              setPermsTouched(true);
              setPendingWildDrop(null);
            }}
          >
            {pendingWildDrop && pendingWildDrop.next < 1 ? "Remove grant-all" : "Reduce grant-all"}
          </Button>
        </DialogActions>
      </Dialog>
      <GroupModal open={groupModal} setGroupModal={(e) => setGroupModal(e)} serviceId={serviceId} setGroupIds={(e) => {
        setGroupIds(e)

      }}
        selectedGroup={props.selectedRole ? props.selectedRole.services.find(s => s.serviceId == serviceId)?.group : {}}
        groupIds={groupIds} />
      <Dialog open={confirmLeave} onClose={() => setConfirmLeave(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Discard your changes?</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14 }}>
            This role has changes that are not saved. If you go back to the roles list now, they will be lost.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmLeave(false)}>Keep editing</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              setConfirmLeave(false);
              props.handleEditClose();
            }}
          >
            Discard changes
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={versionConflict} onClose={() => setVersionConflict(false)} maxWidth="xs" fullWidth>
        <DialogTitle>This role was changed meanwhile</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14 }}>
            Someone else changed this role after you opened it, so your changes were not saved. Reload shows the role as it is now; your unsaved changes are not kept.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVersionConflict(false)}>Keep my changes on screen</Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => {
              setVersionConflict(false);
              if (props.onReloadRole) props.onReloadRole();
            }}
          >
            Reload role
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={pageOutdated} onClose={() => setPageOutdated(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Please reload the page</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14 }}>
            This page is out of date. Reload the page in your browser, then make your change again.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPageOutdated(false)}>Close</Button>
          <Button variant="contained" color="secondary" onClick={() => window.location.reload()}>
            Reload page
          </Button>
        </DialogActions>
      </Dialog>
      <Card>
        <CardContent>
          <Box sx={{ width: "100%" }}>
            <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 0.5 }}>
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={handleBack}
                sx={{ textTransform: "none" }}
              >
                Back to roles
              </Button>
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                borderBottom: 1,
                borderColor: "divider",
              }}
            >
              <Tabs
                style={{ display: "flex", justifyContent: "center" }}
                value={value}
                onChange={handleChange}
                aria-label="basic tabs example"
              >
                <Tab label="Key Information" />
                <Tab label="Base Permissions" />
                <Tab label="Per Solution Permissions" />
              </Tabs>
            </Box>
            {readOnly && (
              <Box
                sx={{
                  mt: 1.5,
                  p: 1.25,
                  borderRadius: 1,
                  bgcolor: "#fff8e1",
                  border: "1px solid #ffb300",
                  color: "#5d4037",
                  textAlign: "center",
                  fontSize: 13,
                  fontWeight: 500,
                }}
              >
                {isOwnRole
                  ? "View only - you cannot modify the role you are assigned to. Changes must be made by another administrator who holds this role, or via the master roles API."
                  : props.selectedRole?.samePrivilege === true
                    ? "View only - this role is at the same privilege level as yours, so it cannot be modified here. A higher-privileged administrator or the master roles API must change it."
                    : "View only - this role exceeds your own access level, so it cannot be modified."}
              </Box>
            )}
            <TabPanel value={value} index={0}>
              <div>
                <div>
                  <TextField
                    label="Name"
                    disabled={readOnly}
                    style={{
                      marginBottom: "10px",
                      width: "100%",
                    }}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </div>
                <div>
                  <TextField
                    label="Description"
                    multiline
                    disabled={readOnly}
                    style={{
                      marginBottom: "10px",
                      width: "100%",
                    }}
                    value={description}
                    rows={12}
                    onChange={(e) => setDescription(e.target.value)}
                  />
                </div>
              </div>
            </TabPanel>
            <TabPanel value={value} index={1}>
              {renderGrantAll(
                "base",
                wildBaseCeiling,
                "Grant all modules - current and future",
                "Every module, including ones created later. Individual rows below are additive."
              )}
              {/* View-only mode: the whole matrix is inert and visibly locked — no level
                  control may even be clicked when nothing could ever be saved.
                  Grant-all(Full): when the role holds '*' modules at Full, every row below is
                  granted at Full via Grant all — the per-row cells are inherited (not editable
                  "Disabled" cells), so the matrix is dimmed + inert to avoid misrepresenting it. */}
              <TableContainer
                component={Paper}
                sx={{ maxHeight: "calc(100vh - 300px)" }}
              >
                {/* The inert/dimmed styling sits on the TABLE, not on the scroll container:
                    pointer-events:none on the container swallowed wheel/touch/scrollbar
                    input, so a view-only role with many rows could not be scrolled at all
                    (bug 2026-09-08). Wheel events on an inert table fall through to the
                    container, which still scrolls. */}
                <Table
                  stickyHeader
                  aria-label="simple table"
                  sx={{
                    pointerEvents: readOnly || anchorGrant.base === 2 ? "none" : "auto",
                    opacity: readOnly ? 0.6 : 1,
                    // Grant-all (Full): every cell is dimmed except the sharing switch cells, which
                    // stay settable (they are not part of Grant all).
                    ...(!readOnly && anchorGrant.base === 2
                      ? { "& th, & td:not(.sharing-switch)": { opacity: 0.6 } }
                      : {}),
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell align="center">Name</TableCell>
                      <TableCell align="center">Overall Permissions</TableCell>
                      <TableCell align="center" colSpan={maxTabColumns}>
                        Permissions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visibleUtils.map(({ u: util, i: utilIndex }) => (
                      <TableRow
                        key={util.name}
                        sx={{
                          "&:last-child td, &:last-child th": { border: 0 },
                        }}
                      >
                        <TableCell align="center">{util.name}</TableCell>
                        <TableCell
                          style={{ backgroundColor: "rgb(245 245 245)" }}
                          align="center"
                        >
                          {/* A row shown only for its sharing switches (the editor holds none
                              of its tabs) offers no Overall control. */}
                          {visibleTabEntries(util).length === 0 ? (
                            <div>-</div>
                          ) : (
                          <div
                            style={{
                              display: "flex",
                              gap: 4,
                              justifyContent: "center",
                            }}
                          >
                            {/* D26 (a): Full is hidden when the row would reach a tab the
                                editor holds only at Partial (it would be silently lowered). */}
                            {wildDispPerm(util.overall, anchorGrant.base) != "ALL" &&
                            scopedEditor &&
                            moduleFullBlocked(util, callerModuleRankFor) ? (
                              HIDDEN_LEVEL_SLOT
                            ) : (
                              <Tooltip
                                title={
                                  moduleOverallGateRank(util) < 2
                                    ? "Raise the required parent-gate permission first"
                                    : "Full Access"
                                }
                              >
                                {wildDispPerm(util.overall, anchorGrant.base) == "ALL" ? (
                                  <CheckCircleIcon color="success" />
                                ) : moduleOverallGateRank(util) < 2 ? (
                                  <CheckCircleOutlinedIcon
                                    sx={{ color: "#ccc", cursor: "not-allowed" }}
                                  />
                                ) : (
                                  <CheckCircleOutlinedIcon
                                    onClick={() =>
                                      utilPermissionChange(
                                        utilIndex,
                                        null,
                                        "ALL",
                                        true
                                      )
                                    }
                                    sx={{ color: "#222", cursor: "pointer" }}
                                  />
                                )}
                              </Tooltip>
                            )}
                            <Tooltip
                              title={
                                anchorGrant.base >= 2 ||
                                (anchorGrant.base >= 1 &&
                                  wildDispPerm(util.overall, anchorGrant.base) === "READ")
                                  ? WILD_BASE_TIP
                                  : moduleOverallGateRank(util) < 1
                                    ? "Raise the required parent-gate permission first"
                                    : "Partial Access"
                              }
                            >
                              {wildDispPerm(util.overall, anchorGrant.base) == "READ" ? (
                                <VisibilityIcon color="warning" />
                              ) : anchorGrant.base >= 2 ||
                                moduleOverallGateRank(util) < 1 ? (
                                <VisibilityOutlinedIcon
                                  sx={{ color: "#ccc", cursor: "not-allowed" }}
                                />
                              ) : (
                                <VisibilityOutlinedIcon
                                  onClick={() =>
                                    utilPermissionChange(
                                      utilIndex,
                                      null,
                                      "READ",
                                      true
                                    )
                                  }
                                  sx={{ color: "#222", cursor: "pointer" }}
                                />
                              )}
                            </Tooltip>
                            <Tooltip title={anchorGrant.base >= 1 ? WILD_BASE_TIP : "Disabled"}>
                              {wildDispPerm(util.overall, anchorGrant.base) == "disable" ? (
                                <CancelIcon color="error" />
                              ) : anchorGrant.base >= 1 ? (
                                <CancelOutlinedIcon
                                  sx={{ color: "#ccc", cursor: "not-allowed" }}
                                />
                              ) : (
                                <CancelOutlinedIcon
                                  onClick={() =>
                                    utilPermissionChange(
                                      utilIndex,
                                      null,
                                      "disable",
                                      true
                                    )
                                  }
                                  sx={{ color: "#222", cursor: "pointer" }}
                                />
                              )}
                            </Tooltip>
                          </div>
                          )}
                        </TableCell>
                        {/* <TableRow> */}
                        {(() => {
                          // Visible (caller-held) tabs carrying their ORIGINAL index into
                          // util.tabs, then the sharing switch cells (right after the row's own
                          // tabs), padded with inert placeholders to the grid width.
                          const entries = visibleTabEntries(util);
                          return [
                            ...entries,
                            ...(switchCellCount(utilIndex) > 0
                              ? SWITCHES.map((switchTab) => ({ switchTab }))
                              : []),
                            ...Array(
                              Math.max(0, maxTabColumns - entries.length - switchCellCount(utilIndex))
                            ).fill({ t: { name: "", permission: "" }, j: -1 }),
                          ];
                        })().map((cell) => {
                          if (cell.switchTab) return renderSwitchCell(cell.switchTab);
                          const { t: tab, j: tabIndex } = cell;
                          // Top-down model: the first-layer GATE parts are always changeable
                          // (lowering cascades with a preview). But a module tab that is a
                          // DEPENDENCY of a selected source (Scheduled Reports needs the
                          // Alarms page tab; solution Group Management ALL needs the
                          // Administration tab) locks below the required level. Amber = auto
                          // granted by a chain; red tint = lowered/deselected by a cascade.
                          const _uKey = `util|${util.name}|${tab.name}`;
                          const _uLock =
                            tab.name && !targetIsUnscoped
                              ? moduleTabLock(utils, servicePermissions, util.name, tab.name)
                              : { level: 0, by: "" };
                          const _uLockRead = _uLock.level >= 2;
                          const _uLockDisable = _uLock.level >= 1;
                          const _uLockTip = `Required by ${_uLock.by}`;
                          // Rank cap: a delegated admin holding a tab at READ cannot offer ALL
                          // (D26 (a): the Full option is then hidden, not greyed).
                          const _uCapAll =
                            scopedEditor &&
                            !!tab.name &&
                            callerModuleRankFor(util.name, tab.name) === 1;
                          // Structural cap (R1/R7): a capped tab greys above its gate's
                          // current rank — the gate must be raised first.
                          const _uGate =
                            tab.name && !targetIsUnscoped
                              ? Math.min(
                                  moduleTabGateRank(utils, util.name, tab.name),
                                  // item 1: base Analytics surface tabs are capped/greyed by
                                  // the Solution Features > Analytics feature rank.
                                  moduleTabSurfaceGateRank(utils, util.name, tab.name)
                                )
                              : Infinity;
                          const _uGateInfo =
                            _uGate < 2 && tab.name
                              ? moduleTabGateInfo(util.name, tab.name) ||
                                moduleTabSurfaceGateInfo(util.name, tab.name)
                              : null;
                          const _uGateTip = _uGateInfo
                            ? `Requires ${_uGateInfo.gateTab} under ${_uGateInfo.gateModule}`
                            : "";
                          // wildFloor: fold the '*' module grant-all onto this tab (display-only).
                          const _uDisp = wildDispPerm(tab.permission, anchorGrant.base);
                          const _uFloorRead = (anchorGrant.base || 0) >= 2; // '*' @Full => READ sub-floor locked
                          const _uFloorDisable = (anchorGrant.base || 0) >= 1; // any '*' => Disabled locked
                          return (
                            <TableCell
                              align="center"
                              style={
                                tab.name && removedCells.has(_uKey)
                                  ? { backgroundColor: "#ffebee" }
                                  : tab.name && autoAssignedCells.has(_uKey)
                                    ? { backgroundColor: "#fff8e1" }
                                    : undefined
                              }
                            >
                              {tab.name ? (
                                <div>
                                  <div style={{ fontSize: 12, color: "#444" }}>
                                    {tab.name}
                                  </div>
                                  <div
                                    style={{
                                      display: "flex",
                                      gap: 4,
                                      justifyContent: "center",
                                    }}
                                  >
                                    {_uDisp != "ALL" && _uCapAll ? (
                                      HIDDEN_LEVEL_SLOT
                                    ) : (
                                      <Tooltip
                                        title={_uGate < 2 ? _uGateTip : "Full Access"}
                                      >
                                        {_uDisp == "ALL" ? (
                                          <CheckCircleIcon color="success" />
                                        ) : _uGate < 2 ? (
                                          <CheckCircleOutlinedIcon
                                            sx={{ color: "#ccc", cursor: "not-allowed" }}
                                          />
                                        ) : (
                                          <CheckCircleOutlinedIcon
                                            onClick={() =>
                                              utilPermissionChange(
                                                utilIndex,
                                                tabIndex,
                                                "ALL",
                                                false
                                              )
                                            }
                                            sx={{
                                              color: "#666",
                                              cursor: "pointer",
                                            }}
                                          />
                                        )}
                                      </Tooltip>
                                    )}
                                    <Tooltip
                                      title={
                                        _uFloorRead ||
                                        (_uDisp === "READ" &&
                                          (anchorGrant.base || 0) >= 1)
                                          ? WILD_BASE_TIP
                                          : _uGate < 1
                                            ? _uGateTip
                                            : _uLockRead
                                              ? _uLockTip
                                              : "Partial Access"
                                      }
                                    >
                                      {_uDisp == "READ" ? (
                                        <VisibilityIcon color="warning" />
                                      ) : _uFloorRead || _uGate < 1 || _uLockRead ? (
                                        <VisibilityOutlinedIcon
                                          sx={{ color: "#ccc", cursor: "not-allowed" }}
                                        />
                                      ) : (
                                        <VisibilityOutlinedIcon
                                          onClick={() =>
                                            utilPermissionChange(
                                              utilIndex,
                                              tabIndex,
                                              "READ",
                                              false
                                            )
                                          }
                                          sx={{
                                            color: "#666",
                                            cursor: "pointer",
                                          }}
                                        />
                                      )}
                                    </Tooltip>
                                    <Tooltip
                                      title={
                                        _uFloorDisable
                                          ? WILD_BASE_TIP
                                          : _uLockDisable
                                            ? _uLockTip
                                            : "Disabled"
                                      }
                                    >
                                      {_uDisp == "disable" ? (
                                        <CancelIcon color="error" />
                                      ) : _uFloorDisable || _uLockDisable ? (
                                        <CancelOutlinedIcon
                                          sx={{ color: "#ccc", cursor: "not-allowed" }}
                                        />
                                      ) : (
                                        <CancelOutlinedIcon
                                          onClick={() =>
                                            utilPermissionChange(
                                              utilIndex,
                                              tabIndex,
                                              "disable",
                                              false
                                            )
                                          }
                                          sx={{
                                            color: "#666",
                                            cursor: "pointer",
                                          }}
                                        />
                                      )}
                                    </Tooltip>
                                  </div>
                                </div>
                              ) : (
                                <div>-</div>
                              )}
                            </TableCell>
                          );
                        })}
                        {/* </TableRow> */}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {renderSharingNote()}
            </TabPanel>
            <TabPanel value={value} index={2}  >
              {renderGrantAll(
                "solution",
                wildSolutionCeiling,
                "Grant all solutions - current and future",
                "Every solution, including ones created later. Individual rows below are additive."
              )}
              {/* Solutions are ordered most-granted first, then alphabetical; the search
                  narrows rows by name (kept usable in view-only mode). */}
              <div style={{ marginBottom: 12 }}>
                <TextField
                  size="small"
                  placeholder="Search solutions by name"
                  value={solutionSearch}
                  onChange={(e) => setSolutionSearch(e.target.value)}
                  style={{ minWidth: 260 }}
                />
              </div>
              {/* View-only mode: inert + visibly locked (same as the utilities matrix).
                  NOTE: unlike the module matrix, the solution matrix is NOT dimmed on '*'
                  solution @Full - its per-row Groups control is a DISTINCT axis that '*'
                  solution does NOT subsume, so dimming the whole container would wrongly
                  disable it. The feature/asset cells are inherited via '*' and now folded
                  PER-CELL via wildFloor (wildDispPerm: max(own, floor) with the sub-floor levels
                  locked) - see the wildDispPerm helper above - so the container stays
                  interactive for the Groups axis while feature cells read as granted. */}
              <TableContainer
                component={Paper}
                sx={{ maxHeight: "calc(100vh - 300px)", width: "92vw" }}
              >
                {/* Inert/dimmed styling on the TABLE, not the scroll container (see the
                    utilities matrix above): the container must keep receiving scroll input. */}
                <Table
                  stickyHeader
                  aria-label="simple table"
                  sx={{
                    pointerEvents: readOnly ? "none" : "auto",
                    opacity: readOnly ? 0.65 : 1,
                  }}
                >
                  <TableHead  >
                    <TableRow>
                      <TableCell align="center">Name</TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          "& .mh-reset": { display: "none" },
                          "&:hover .mh-reset": { display: "inline-flex" },
                          "&:hover .mh-label": { display: "none" },
                        }}
                      >
                        <span className="mh-label">Groups</span>
                        <span className="mh-reset">
                          <Button onClick={resetGroupsToAssets}>Reset</Button>
                        </span>
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          ...masterHeaderSx,
                          "&:hover": { backgroundColor: "rgb(245, 245, 245)" },
                        }}
                      >
                        <span className="mh-label">Overall Feature Permissions</span>
                        <div className="mh-master">
                          <AccessType asset={"Overall Permissions"} />
                        </div>
                      </TableCell>
                      {visibleAssets.map((asset) => {
                        return (
                          <TableCell key={asset} align="center" sx={masterHeaderSx}>
                            <span className="mh-label">
                              {asset === "Scheduled Reports" ? (
                                <Tooltip title="Scopes which solutions this role's scheduled reports may cover. Partial Access makes the solution eligible.">
                                  <span>{asset}</span>
                                </Tooltip>
                              ) : asset === "Threshold Profiles" ? (
                                <Tooltip title="Scopes threshold template access per solution. Partial Access is view only, Full Access can manage and apply.">
                                  <span>{asset}</span>
                                </Tooltip>
                              ) : (
                                asset
                              )}
                            </span>
                            <div className="mh-master">
                              <AccessType asset={asset} />
                            </div>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visibleServices.map(({ s: service, i: serviceIndex }) => (
                      <TableRow
                        key={service.name}
                        sx={{
                          "&:last-child td, &:last-child th": { border: 0 },
                        }}
                      >
                        <TableCell align="center">{service.name}</TableCell>
                        <TableCell align="center">
                          <Chip
                            label={groupIds.find(g => g.serviceId == service.id)?.name || "All assets"}
                            onClick={() => handleGroupClick(service.id)}
                          />
                        </TableCell>
                        <TableCell
                          style={{ backgroundColor: "rgb(245 245 245)" }}
                          align="center"
                        >
                          <div
                            style={{
                              display: "flex",
                              gap: 4,
                              justifyContent: "center",
                            }}
                          >
                            {/* D26 (a): Full is hidden when the row holds a feature the
                                editor has only at Partial (it would be silently lowered). */}
                            {wildDispPerm(service.overall, anchorGrant.solution) != "ALL" &&
                            scopedEditor &&
                            bulkFullBlocked([service], null, callerRankFor) ? (
                              HIDDEN_LEVEL_SLOT
                            ) : (
                              <Tooltip title="Full Access">
                                {wildDispPerm(service.overall, anchorGrant.solution) == "ALL" ? (
                                  <CheckCircleIcon color="success" />
                                ) : (
                                  <CheckCircleOutlinedIcon
                                    onClick={() =>
                                      servicePermissionChange(
                                        serviceIndex,
                                        null,
                                        "ALL",
                                        true
                                      )
                                    }
                                    sx={{ color: "#222", cursor: "pointer" }}
                                  />
                                )}
                              </Tooltip>
                            )}
                            <Tooltip
                              title={
                                anchorGrant.solution >= 2 ||
                                (anchorGrant.solution >= 1 &&
                                  wildDispPerm(service.overall, anchorGrant.solution) ===
                                    "READ")
                                  ? WILD_SOL_TIP
                                  : "Partial Access"
                              }
                            >
                              {wildDispPerm(service.overall, anchorGrant.solution) == "READ" ? (
                                <VisibilityIcon color="warning" />
                              ) : anchorGrant.solution >= 2 ? (
                                <VisibilityOutlinedIcon
                                  sx={{ color: "#ccc", cursor: "not-allowed" }}
                                />
                              ) : (
                                <VisibilityOutlinedIcon
                                  onClick={() =>
                                    servicePermissionChange(
                                      serviceIndex,
                                      null,
                                      "READ",
                                      true
                                    )
                                  }
                                  sx={{ color: "#222", cursor: "pointer" }}
                                />
                              )}
                            </Tooltip>
                            <Tooltip
                              title={
                                anchorGrant.solution >= 1 ? WILD_SOL_TIP : "Disabled"
                              }
                            >
                              {wildDispPerm(service.overall, anchorGrant.solution) == "disable" ? (
                                <CancelIcon color="error" />
                              ) : anchorGrant.solution >= 1 ? (
                                <CancelOutlinedIcon
                                  sx={{ color: "#ccc", cursor: "not-allowed" }}
                                />
                              ) : (
                                <CancelOutlinedIcon
                                  onClick={() =>
                                    servicePermissionChange(
                                      serviceIndex,
                                      null,
                                      "disable",
                                      true
                                    )
                                  }
                                  sx={{ color: "#222", cursor: "pointer" }}
                                />
                              )}
                            </Tooltip>
                          </div>
                        </TableCell>
                        {visibleAssets.map((asset, assetIndex) => {
                          const _autoKey = `svc|${service.serviceId}|${asset}`;
                          // Item 5b/7: a scoped editor only gets controls on cells they hold
                          // themselves (unheld -> inert dash); held-at-READ caps out ALL
                          // (D26 (a): the Full option is then hidden, not greyed).
                          const _cellHeld =
                            !scopedEditor ||
                            callerRankFor(service.serviceId, asset) >= 1;
                          const _capAll =
                            scopedEditor &&
                            callerRankFor(service.serviceId, asset) === 1;
                          // Item 5: while "Threshold Management" is held, its dependency assets
                          // (Controlling, Rules) are locked at/above the required level on
                          // a held row — the level controls BELOW that are greyed with a tooltip.
                          // The lock lifts automatically once Threshold Management is removed
                          // (lockedDepLevel then returns 0). The row-level Overall control above is
                          // NOT locked, so a whole solution can still be removed.
                          const _rowHeld = (service.assets || []).some(
                            (a) => a && permRank(a.permissions) >= 1
                          );
                          const _depLock =
                            _rowHeld && !targetIsUnscoped
                              ? cellDepLock(
                                  utils,
                                  service,
                                  asset,
                                  callerRankFor(service.serviceId, asset)
                                )
                              : { level: 0, by: "" };
                          const _lockLvl = _depLock.level;
                          const _lockPartial = _lockLvl >= 2; // needs ALL -> READ blocked
                          const _lockDisable = _lockLvl >= 1; // needs a grant -> Disable blocked
                          const _lockTip = `Required by ${_depLock.by}`;
                          // Top-down gate: a gated asset's controls ABOVE the utility part's
                          // current rank are greyed until the part is granted first.
                          const _gate = targetIsUnscoped
                            ? Infinity
                            : gateRankFor(utils, asset);
                          const _gateInfo = _gate < 2 ? gateInfoFor(asset) : null;
                          const _gateAll = _gate < 2;
                          const _gateRead = _gate < 1;
                          const _gateTip = _gateInfo
                            ? `Requires ${_gateInfo.tab} under ${_gateInfo.module}`
                            : "";
                          // Scoping columns (Scheduled Reports / Threshold Profiles) scope
                          // the row's features: raises grey out on a feature-less row.
                          const _scoping = isScopingAsset(asset);
                          const _opRowHeld = (service.assets || []).some(
                            (a) =>
                              a &&
                              !isScopingAsset(a.name) &&
                              permRank(a.permissions) >= 1
                          );
                          const _scopeBlocked = _scoping && !_opRowHeld && !targetIsUnscoped;
                          const _scopeTip =
                            "Grant a feature permission on this solution first";
                          // wildFloor: fold the '*' solution grant-all onto this feature cell
                          // (display-only). ROLE_ASSETS == the BE GRANT_ALL_SERVICE_TABS set, so
                          // every asset column inherits the '*' service floor (the Groups chip is
                          // a separate cell and is NOT folded).
                          const _aPerm = service.assets.find((a) => a.name == asset)?.permissions;
                          const _aDisp = wildDispPerm(_aPerm, anchorGrant.solution);
                          const _aFloorRead = (anchorGrant.solution || 0) >= 2; // '*' @Full => READ locked
                          const _aFloorDisable = (anchorGrant.solution || 0) >= 1; // any '*' => Disabled locked
                          return (
                            <TableCell
                              align="center"
                              // Amber = auto granted by a dependency chain; red tint = auto
                              // deselected or lowered by the master-layer cascade.
                              style={
                                removedCells.has(_autoKey)
                                  ? { backgroundColor: "#ffebee" }
                                  : autoAssignedCells.has(_autoKey)
                                    ? { backgroundColor: "#fff8e1" }
                                    : undefined
                              }
                            >
                              {_cellHeld &&
                              (service.assets.find((a) => a.name == asset) ||
                                asset == "Monitoring" ||
                                asset == "Controlling" ||
                                asset == "Alarms" ||
                                asset == "Events" ||
                                asset == "Analytics" ||
                                asset == "History" ||
                                asset == "Metadata" ||
                                asset == "Configuration" ||
                                asset == "Threshold Profiles" ||
                                asset == "Scheduled Reports" ||
                                asset == "Group Management") ? (
                                <div
                                  style={{
                                    display: "flex",
                                    gap: 4,
                                    justifyContent: "center",
                                  }}
                                >
                                  {_aDisp != "ALL" && _capAll ? (
                                    HIDDEN_LEVEL_SLOT
                                  ) : (
                                    <Tooltip
                                      title={
                                        _scopeBlocked
                                          ? _scopeTip
                                          : _gateAll
                                            ? _gateTip
                                            : "Full Access"
                                      }
                                    >
                                      {_aDisp == "ALL" ? (
                                        <CheckCircleIcon color="success" />
                                      ) : _scopeBlocked || _gateAll ? (
                                        <CheckCircleOutlinedIcon
                                          sx={{ color: "#ccc", cursor: "not-allowed" }}
                                        />
                                      ) : (
                                        <CheckCircleOutlinedIcon
                                          onClick={() =>
                                            servicePermissionChange(
                                              serviceIndex,
                                              asset,
                                              "ALL",
                                              false
                                            )
                                          }
                                          sx={{
                                            color: "#666",
                                            cursor: "pointer",
                                          }}
                                        />
                                      )}
                                    </Tooltip>
                                  )}
                                  <Tooltip
                                    title={
                                      _aFloorRead ||
                                      (_aDisp === "READ" &&
                                        (anchorGrant.solution || 0) >= 1)
                                        ? WILD_SOL_TIP
                                        : _scopeBlocked
                                          ? _scopeTip
                                          : _lockPartial
                                            ? _lockTip
                                            : _gateRead
                                              ? _gateTip
                                              : "Partial Access"
                                    }
                                  >
                                    {_aDisp == "READ" ? (
                                      <VisibilityIcon color="warning" />
                                    ) : _aFloorRead ||
                                      _scopeBlocked ||
                                      _lockPartial ||
                                      _gateRead ? (
                                      <VisibilityOutlinedIcon
                                        sx={{ color: "#ccc", cursor: "not-allowed" }}
                                      />
                                    ) : (
                                      <VisibilityOutlinedIcon
                                        onClick={() =>
                                          servicePermissionChange(
                                            serviceIndex,
                                            asset,
                                            "READ",
                                            false
                                          )
                                        }
                                        sx={{
                                          color: "#666",
                                          cursor: "pointer",
                                        }}
                                      />
                                    )}
                                  </Tooltip>
                                  <Tooltip
                                    title={
                                      _aFloorDisable
                                        ? WILD_SOL_TIP
                                        : _lockDisable
                                          ? _lockTip
                                          : "Disabled"
                                    }
                                  >
                                    {_aDisp == "disable" ? (
                                      <CancelIcon color="error" />
                                    ) : _aFloorDisable || _lockDisable ? (
                                      <CancelOutlinedIcon
                                        sx={{ color: "#ccc", cursor: "not-allowed" }}
                                      />
                                    ) : (
                                      <CancelOutlinedIcon
                                        onClick={() =>
                                          servicePermissionChange(
                                            serviceIndex,
                                            asset,
                                            "disable",
                                            false
                                          )
                                        }
                                        sx={{
                                          color: "#666",
                                          cursor: "pointer",
                                        }}
                                      />
                                    )}
                                  </Tooltip>
                                </div>
                              ) : (
                                <div
                                  style={{
                                    display: "flex",
                                    gap: 4,
                                    justifyContent: "center",
                                  }}
                                >
                                  -
                                </div>
                              )}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </TabPanel>
          </Box>
          <div
            style={{
              display: "flex",
              gap: "20px",
              justifyContent: "center",
              margin: "6px 20px",
            }}
          >
            {readOnly && (
              <span
                style={{ alignSelf: "center", marginRight: 8, fontSize: 12, color: "#9e9e9e" }}
              >
                {isOwnRole
                  ? "You can only view your own role"
                  : "You can only view this role"}
              </span>
            )}
            {readOnly && (switches[SHARE] || 0) >= 1 && !creationOk && (
              <span
                style={{ alignSelf: "center", fontSize: 12, color: "#9e9e9e" }}
                data-testid="share-view-only-note"
              >
                {SHARE_VIEW_ONLY_NOTE}
              </span>
            )}
            {!readOnly && shareOffNote && (
              <span
                style={{ alignSelf: "center", fontSize: 12, color: "#b26a00" }}
                data-testid="share-switched-off-note"
              >
                {SHARE_SWITCHED_OFF_NOTE}
              </span>
            )}
            {/* Announced to screen readers when it appears: a live region present from the start (one
                added together with its text is often not read out), out of the layout. */}
            <span
              role="status"
              aria-live="polite"
              data-testid="share-switched-off-live"
              style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)", whiteSpace: "nowrap" }}
            >
              {!readOnly && shareOffNote ? SHARE_SWITCHED_OFF_NOTE : ""}
            </span>
            {/* Rule of thumb: a privilege-gated action with no view alternative is HIDDEN,
                not greyed. In the view-only mode the hint above explains the state; the
                validation/in-flight disables stay for editors. */}
            {/* Reset and Update are greyed out (not hidden) until the role differs from its
                starting point: a state-based limit, unlike the privilege-based hide above.
                Create and Clone keep the validation-only rule: an unchanged clone is valid. */}
            {!readOnly && (
              <Button
                disabled={!isDirty || isLoading}
                onClick={resetToBaseline}
                variant="outlined"
                color="secondary"
              >
                Reset
              </Button>
            )}
            {!readOnly && (
              <Button
                disabled={
                  !name ||
                  !description ||
                  isLoading ||
                  (!!props.selectedRole && !props.isCloneRole && !isDirty)
                }
                onClick={() => addRole()}
                variant="contained"
                color="secondary"
              >
                {isLoading && props.isCloneRole
                  ? "Cloning"
                  : !isLoading && props.isCloneRole
                    ? "Clone"
                    : isLoading && props.selectedRole
                      ? "Updating"
                      : !isLoading && props.selectedRole
                        ? "Update"
                        : "Submit"}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </Fragment>
  );
}
